import { useEffect, useRef, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useSlotGroup,
  EditableCollectionBinding,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage, getMediaUrl, parseStorageKey, videoThumbStorageKey } from '@vibesites/ui-primitives';
import { blurbDefault, clean, fmtN, money, noPeriod, toParagraph } from '../utils/quinn';
import { useRawProperty, useReducedMotion } from '../utils/hooks';
import { toPlanCards, type Floorplan } from '../utils/floorplans';
import { feeSchedule, fmtCents } from '../utils/fees';

const FLOOR_PLANS_PATH = '/floor-plans';

const backgroundDef: SlotGroupDefinition = {
  label: 'Background Media',
  description: 'If a video is set, it plays as the background. Otherwise the image is used.',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'property_featured_image' },
    video: { label: 'Video', type: 'video', defaultField: 'property_featured_video' },
  },
};

const allHomesDef: SlotGroupDefinition = {
  label: 'All Homes Link',
  fields: {
    label: { label: 'Link Text', type: 'text', defaultValue: 'See All Available Homes' },
    short_label: { label: 'Link Text (phone)', type: 'text', defaultValue: 'All Homes' },
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

export function Hero() {
  const p = useRawProperty();
  const reduced = useReducedMotion();
  const bg = useSlotGroup('hero.background', backgroundDef);
  const allHomes = useSlotGroup('hero.all_homes', allHomesDef);
  const plans = useCollectionBinding<Floorplan>('hero.featured_homes', { collection: 'floorplans' });

  const romance = clean(p.property_home_romance_title);
  const titleDefault = romance ? toParagraph(noPeriod(romance)) : blurbDefault(p.property_short_description, 'title');
  const homes = toPlanCards(plans)
    .filter((c) => c.avail > 0 && c.price)
    .sort((a, b) => b.avail - a.avail)
    .slice(0, 3);
  const monthlyCharges = feeSchedule(p).monthlyLo;

  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const showVideo = !!bg.video && !reduced;
  const thumbKey = videoThumbStorageKey(parseStorageKey(bg.video || null));
  const poster = bg.image || (thumbKey ? getMediaUrl(thumbKey, { width: 1920 }) : null) || '';

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (reduced) {
      v.pause();
      return;
    }
    const tryPlay = () => v.play()?.catch(() => {});
    tryPlay();
    const events = ['touchstart', 'pointerdown', 'scroll', 'keydown'];
    const retry = () => v.paused && tryPlay();
    events.forEach((ev) => window.addEventListener(ev, retry, { passive: true, once: true }));
    return () => events.forEach((ev) => window.removeEventListener(ev, retry));
  }, [reduced, bg.video]);

  const hasMedia = !!(bg.image || bg.video);

  return (
    <section
      className={`relative flex h-[88svh] min-h-[680px] w-full flex-col justify-end overflow-hidden bg-[--q-primary] max-[1199px]:h-auto max-[1199px]:min-h-[72svh] ${hasMedia ? 'text-[--q-on-image] [&_.eyebrow--on-primary]:text-[--q-on-image] [&_.textlink--on-primary]:text-[--q-on-image] max-[1199px]:[&_.featured_.eyebrow--on-primary]:text-[--q-on-primary] max-[1199px]:[&_.featured_.textlink--on-primary]:text-[--q-on-primary]' : 'border-[--q-on-primary] text-[--q-on-primary] [&_.eyebrow--on-primary]:border-[--q-on-primary] [&_.eyebrow--on-primary]:text-[--q-on-primary] [&_.textlink--on-primary]:border-[--q-on-primary] [&_.textlink--on-primary]:text-[--q-on-primary]'}`}
    >
      <EditableSlotGroup
        groupPath="hero.background"
        definition={backgroundDef}
        className={`absolute inset-0 h-full w-full object-cover [&>img]:absolute [&>img]:inset-0 [&>img]:h-full [&>img]:w-full [&>img]:object-cover${!showVideo && !reduced ? ' [&>img]:origin-[50%_60%] [&>img]:animate-[drift_var(--q-hero-drift)_ease-in-out_infinite_alternate] motion-reduce:[&>img]:[transform:none]' : ''}`}
      >
        {bg.image && <VibesitesImage url={bg.image} alt="" priority sizes="100vw" />}
        {showVideo && (
          <video
            ref={videoRef}
            className={`absolute inset-0 h-full w-full object-cover [transition:opacity_var(--q-motion-slow)_ease] ${playing ? 'opacity-100' : 'opacity-0'}`}
            src={bg.video!}
            poster={poster}
            autoPlay
            muted
            loop
            playsInline
            preload="auto"
            aria-hidden
            onPlaying={() => setPlaying(true)}
          />
        )}
      </EditableSlotGroup>
      <div
        className="absolute inset-0 bg-[linear-gradient(180deg,color-mix(in_srgb,var(--q-ink)_55%,transparent)_0%,color-mix(in_srgb,var(--q-ink)_10%,transparent)_22%,transparent_34%,color-mix(in_srgb,var(--q-ink)_55%,transparent)_62%,color-mix(in_srgb,var(--q-ink)_86%,transparent)_100%)] max-[767px]:bg-[linear-gradient(180deg,color-mix(in_srgb,var(--q-ink)_55%,transparent)_0%,color-mix(in_srgb,var(--q-ink)_15%,transparent)_18%,color-mix(in_srgb,var(--q-ink)_35%,transparent)_38%,color-mix(in_srgb,var(--q-ink)_78%,transparent)_60%,color-mix(in_srgb,var(--q-ink)_92%,transparent)_100%)]"
        aria-hidden="true"
      />

      <div className="relative z-[2] flex flex-col items-start gap-6 px-[--gutter] pb-7 pt-0 max-[1199px]:pt-40 max-[767px]:w-auto max-[767px]:gap-3 max-[767px]:pt-[140px]">
        <RichTextContent
          path="hero.title"
          as="h1"
          className="m-0 font-heading text-6xl font-light leading-[1.07] tracking-[--q-tracking-tight] max-[767px]:text-[40px] max-[767px]:leading-[44px]"
          defaultValue={titleDefault}
        />
      </div>

      <EditableCollectionBinding bindingPath="hero.featured_homes" label="Ready-Now Homes" className={homes.length > 0 ? 'relative z-[2]' : ''}>
        {homes.length > 0 && (
          <aside className="featured">
            <div className="flex items-baseline justify-between px-1 pb-1.5 pt-0 max-[767px]:flex-nowrap max-[767px]:gap-3">
              <RichTextContent path="hero.featured_label" as="div" className="eyebrow eyebrow--on-primary max-[767px]:whitespace-nowrap" />
              <EditableSlotGroup groupPath="hero.all_homes" definition={allHomesDef}>
                <a
                  className="textlink textlink--on-primary max-[767px]:whitespace-nowrap max-[767px]:[&.textlink]:text-[12px]"
                  href={allHomes.url || FLOOR_PLANS_PATH}
                  target={allHomes._target || '_self'}
                >
                  <span className="max-[767px]:hidden">{allHomes.label || 'See All Available Homes'}</span>
                  <span className="hidden max-[767px]:inline">{allHomes.short_label || 'All Homes'}</span> <span className="btn__arrow">→</span>
                </a>
              </EditableSlotGroup>
            </div>
            <div className="grid grid-cols-[repeat(3,1fr)] gap-3 max-[1199px]:mx-[calc(-1*var(--gutter))] max-[1199px]:my-0 max-[1199px]:flex max-[1199px]:gap-2.5 max-[1199px]:overflow-x-auto max-[1199px]:px-[--gutter] max-[1199px]:py-0 max-[1199px]:[scroll-padding:0_var(--gutter)] max-[1199px]:[scroll-snap-type:x_mandatory] max-[1199px]:[scrollbar-width:none] max-[1199px]:[&::-webkit-scrollbar]:hidden max-[767px]:flex-row">
              {homes.map((h) => (
                <a
                  className="flex h-[72px] items-center gap-3.5 rounded-md border border-[color-mix(in_srgb,var(--q-paper)_40%,transparent)] bg-[color-mix(in_srgb,var(--q-paper)_92%,transparent)] py-0 pl-2.5 pr-4 text-[--q-ink] no-underline [backdrop-filter:blur(12px)] [transition:transform_var(--q-motion-normal)_var(--q-ease-out),background-color_var(--q-motion-fast)] hover:bg-[--q-paper] hover:[transform:translateY(-2px)] motion-reduce:hover:![transform:none] max-[1199px]:h-auto max-[1199px]:min-h-[68px] max-[1199px]:w-[322px] max-[1199px]:shrink-0 max-[1199px]:snap-start max-[1199px]:py-2"
                  href={FLOOR_PLANS_PATH}
                  key={h.key}
                >
                  <span className="h-[52px] w-[52px] shrink-0 overflow-hidden rounded-sm bg-[--q-plan-surface] [&_img]:h-full [&_img]:w-full [&_img]:object-cover">
                    {h.img && <VibesitesImage url={h.img} alt="" sizes="52px" />}
                  </span>
                  <span className="flex min-w-0 grow flex-col gap-px">
                    <span className="text-[15px] font-medium leading-5">{h.name}</span>
                    <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[12px] leading-4 text-[--q-muted] max-[1199px]:overflow-visible max-[1199px]:text-clip max-[1199px]:whitespace-normal">
                      {[h.beds != null && `${h.beds} bd`, h.baths != null && `${h.baths} ba`].filter(Boolean).join(' · ')}
                    </span>
                    {h.sqft && (
                      <span className="overflow-hidden text-ellipsis whitespace-nowrap text-[12px] leading-4 text-[--q-muted] max-[1199px]:overflow-visible max-[1199px]:text-clip max-[1199px]:whitespace-normal">
                        {fmtN(h.sqft)} sq ft
                      </span>
                    )}
                  </span>
                  <span className="flex max-w-[132px] shrink-0 flex-wrap items-baseline justify-end gap-x-[3px] gap-y-0 pl-3">
                    <small className="w-full text-right text-[11px] leading-[14px] text-[--q-muted]">{monthlyCharges ? 'Total monthly price' : 'Base rent'}</small>
                    <b className="font-heading text-[20px] font-normal leading-6 text-[--q-secondary]">{monthlyCharges ? fmtCents(h.price! + monthlyCharges) : money(h.price)}</b>
                    <small className="text-[11px] text-[--q-muted]">/mo</small>
                    {monthlyCharges ? <small className="w-full text-right text-[11px] leading-[14px] text-[--q-muted]">Base rent {money(h.price)}</small> : null}
                  </span>
                </a>
              ))}
            </div>
          </aside>
        )}
      </EditableCollectionBinding>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero',
  tokens: {
    'hero.title': {
      label: 'Headline',
      description: 'Until edited, shows the home romance title without its closing period; without one, the bold first sentence of the short description.',
      type: 'richtext',
      defaultValue: '<p>Rental-home living, thoughtfully built</p>',
      suggestedFields: ['property_home_romance_title', 'property_short_description', 'property_long_description'],
      generator: {
        prompt: 'A short headline that says what makes this community worth a look: the kind of homes and where they are. No closing period.',
        context: 'H1 over the full-bleed home page hero photo. Large display type, so keep it to two lines.',
        length: '4–9 words',
      },
    },
    'hero.featured_label': { label: 'Ready-Now Label', type: 'richtext', defaultValue: '<p>Ready to move in now</p>' },
  },
  slotGroups: {
    'hero.background': backgroundDef,
    'hero.all_homes': allHomesDef,
  },
  collectionBindings: {
    'hero.featured_homes': {
      label: 'Ready-Now Homes',
      collection: 'floorplans',
    },
  },
};

(Hero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Hero;
